import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/useAuthStore';
import { supabase } from '@/lib/supabase';
import { Disclaimer } from '@/components/ui/Disclaimer';
import { isGerman } from '@/i18n/locale';
import StartDE from './StartDE';

// ════════════════════════════════════════════════════════════════════
// /start — anonymous onboarding
//
// Two fields, fifteen seconds, no signup. The mum signs in
// anonymously (Supabase creates a real auth.users row with
// is_anonymous=true), we insert her baby into public.babies, and
// route her straight to /chat where Alma already knows the baby's age.
//
// If she later converts to a real account via the SignupWall (Phase 3B),
// her user_id is preserved by Supabase identity-linking, so the baby
// and the conversation carry over to the linked account with zero
// migration.
// ════════════════════════════════════════════════════════════════════

// Today's date in YYYY-MM-DD, used as max for the DOB picker so mums
// can't pick a future date by accident.
function todayISO(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// 24 months ago — Alma is currently scoped to 0–24mo, so we soft-cap
// the picker at this lower bound. Older babies still get an answer
// ("Alma covers 0–24mo today; we'll widen the window soon") but at
// least we don't pretend to be tuned for them.
function twoYearsAgoISO(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 2);
  d.setDate(d.getDate() - 30); // small buffer for early-stage product
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export default function Start() {
  // German speakers get the German onboarding. Same /start route,
  // detected via navigator.language at render time.
  if (isGerman()) return <StartDE />;
  return <StartEN />;
}

function StartEN() {
  const navigate = useNavigate();
  const signInAnonymously = useAuthStore((s) => s.signInAnonymously);

  // If the visitor came from a landing "Ask Alma" question card, the
  // question is in the ?prompt= URL param. We pass it through after
  // onboarding so /chat auto-sends it (see ChatWindow's auto-send hook).
  const [searchParams] = useSearchParams();
  const incomingPrompt = searchParams.get('prompt');

  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // iOS Safari renders <input type="date"> with no native placeholder
  // when empty — the field just looks like a blank rectangle. Every
  // other browser shows a locale-aware hint like "tt.mm.jjjj" or
  // "mm/dd/yyyy". We detect iOS via userAgent and only overlay our own
  // hint when running on iOS, so the field has visual balance there
  // without colliding with native placeholders on desktop/Android.
  //
  // Note: iPadOS in "Request Desktop Site" mode reports a Mac userAgent
  // and will miss this overlay. Acceptable trade-off — that user
  // actively chose desktop mode and will see the desktop fallback.
  const isIOS = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return /iPhone|iPad|iPod/.test(navigator.userAgent);
  }, []);

  const canSubmit = name.trim().length > 0 && dob.length > 0 && !submitting;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);

    try {
      // 1. Create anonymous session.
      const { error: signInErr, user } = await signInAnonymously();
      if (signInErr || !user) {
        throw new Error(signInErr ?? 'Could not start a session. Please try again.');
      }

      // 2. Insert baby. RLS allows: auth.uid() = user_id for INSERT.
      //    Direct supabase call (not an API endpoint) because we want
      //    this to feel instant and the operation is trivially scoped
      //    to the user's own data.
      const { error: babyErr } = await supabase
        .from('babies')
        .insert({
          user_id: user.id,
          name: name.trim(),
          date_of_birth: dob,
          is_active: true,
        });
      if (babyErr) {
        throw new Error(babyErr.message ?? 'Could not save your baby. Please try again.');
      }

      // 3. Off to the races. /chat will pick up the baby on mount.
      //    If we have a prompt from a landing question card, forward it
      //    so /chat auto-sends and the mum sees Alma answer her actual
      //    question without typing it twice.
      const dest = incomingPrompt
        ? `/chat?prompt=${encodeURIComponent(incomingPrompt)}`
        : '/chat';
      navigate(dest, { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong.';
      setError(msg);
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-[100dvh] bg-cream flex flex-col">
      {/* Tiny nav so people don't feel trapped */}
      <nav className="mx-auto w-full max-w-6xl px-6 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center shadow-sm">
            <span className="text-white text-base font-display font-semibold">A</span>
          </div>
          <span className="font-display text-xl font-semibold text-slate tracking-tight">
            Alma
          </span>
        </Link>
        <Link to="/login" className="btn-ghost text-sm">
          I have an account
        </Link>
      </nav>

      <main className="flex-1 flex items-center justify-center px-6 pb-10">
        <div className="w-full max-w-md">
          {/* ─────────────────────────────────────────────────────────
              Soft organic background — same family as Landing hero.
              ───────────────────────────────────────────────────────── */}
          <div
            aria-hidden
            className="pointer-events-none fixed inset-0 -z-10"
            style={{
              background:
                'radial-gradient(60rem 40rem at 85% -10%, rgba(232,164,160,0.16) 0%, transparent 60%), radial-gradient(50rem 35rem at -10% 110%, rgba(143,181,158,0.16) 0%, transparent 55%)',
            }}
          />

          <div className="text-center mb-8">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-700 mb-3">
              Fifteen seconds
            </div>
            <h1 className="font-display text-4xl sm:text-[44px] font-semibold tracking-tight text-slate leading-[1.05]">
              Tell me about your baby.
            </h1>
            <p className="mt-4 text-base text-slate-muted leading-relaxed">
              Two questions. No signup. Alma tunes every answer to your baby's exact age,
              so this matters.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="rounded-2xl bg-white border border-primary-100 p-6 sm:p-7 shadow-sm space-y-5"
          >
            <div>
              <label
                htmlFor="baby-name"
                className="block text-sm font-medium text-slate mb-2"
              >
                Baby's name
              </label>
              <input
                id="baby-name"
                type="text"
                inputMode="text"
                autoComplete="off"
                autoFocus
                placeholder="e.g. Lena"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={submitting}
                maxLength={40}
                className="w-full rounded-xl border border-primary-100 bg-cream/40 px-4 py-3 text-base text-slate placeholder:text-slate-muted/60 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition disabled:opacity-50"
              />
              <p className="mt-1.5 text-[12px] text-slate-muted">
                First name is enough — Alma uses it to make answers feel personal.
              </p>
            </div>

            <div>
              <label
                htmlFor="baby-dob"
                className="block text-sm font-medium text-slate mb-2"
              >
                Date of birth
              </label>
              {/*
                Date input — on iOS Safari, type="date" renders no native
                placeholder, so we overlay a "MM/DD/YYYY" hint (US date order) to match the
                visual weight of the name field above. On desktop and
                Android, browsers show their own locale-aware placeholder
                (e.g. "tt.mm.jjjj" in German), so we skip our overlay there
                to avoid collision. iOS detection via isIOS above.
              */}
              <div className="relative">
                <input
                  id="baby-dob"
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  disabled={submitting}
                  min={twoYearsAgoISO()}
                  max={todayISO()}
                  className="w-full rounded-xl border border-primary-100 bg-cream/40 px-4 py-3 text-base text-slate focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition disabled:opacity-50"
                />
                {!dob && isIOS && (
                  <span
                    aria-hidden
                    className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base text-slate-muted/50"
                  >
                    MM/DD/YYYY
                  </span>
                )}
              </div>
              <p className="mt-1.5 text-[12px] text-slate-muted">
                Alma tunes advice to your baby’s exact age — currently 0–24 months.
              </p>
            </div>

            {error && (
              <div className="rounded-xl border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full rounded-xl bg-primary text-white font-medium text-base py-3.5 hover:bg-primary-600 active:scale-[0.99] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
            >
              {submitting ? 'Setting things up…' : 'Start chatting →'}
            </button>

            <p className="text-center text-[12px] text-slate-muted">
              Free for 3 questions. No card. No email.
            </p>
          </form>

          <div className="mt-8">
            <Disclaimer className="text-center text-slate-muted text-[11px] leading-relaxed" />
          </div>
        </div>
      </main>
    </div>
  );
}
